"use client";

import {
    useRef,
    useEffect,
    useState,
    useCallback,
    useMemo,
} from "react";
import type { SmokeData } from "@/lib/api";

// ─── Props ─────────────────────────────────────────────────────

interface SmokeGraphProps {
    data: SmokeData | null;
    loading?: boolean;
    height?: number;
    compact?: boolean;
    showLegend?: boolean;
}

interface TooltipState {
    x: number;
    y: number;
    timestamp: number;
    values: Record<string, number | null>;
    packetLoss: number | null;
}

// ─── Constants ─────────────────────────────────────────────────────

const PERCENTILE_KEYS = ["p50", "p75", "p90", "p95", "p99"] as const;

/** Smoke band pairs — outermost (lightest) to innermost (densest). */
const SMOKE_BANDS = [
    { outer: "p99", inner: "p95", alpha: 0.10 },
    { outer: "p95", inner: "p90", alpha: 0.18 },
    { outer: "p90", inner: "p75", alpha: 0.28 },
    { outer: "p75", inner: "p50", alpha: 0.42 },
];

/**
 * SmokePing-faithful loss → color scale.
 *
 * Classic SmokePing encodes packet loss as hue, rotating through
 * the cool spectrum: green → teal → blue → indigo → violet → magenta → red.
 *
 * This is the defining visual signature — you glance at the graph
 * and instantly see "it went purple at 3am" = packets were dropping.
 *
 * Format: [loss_percent, R, G, B]
 */
const LOSS_COLOR_STOPS: [number, number, number, number][] = [
    [0,    38, 198,  98],   // 0%   — green (healthy)
    [1,     0, 190, 140],   // 1%   — teal-green
    [3,     0, 166, 200],   // 3%   — cyan
    [6,     0, 120, 218],   // 6%   — blue
    [10,   55,  55, 210],   // 10%  — indigo
    [20,  130,   0, 200],   // 20%  — violet
    [35,  185,   0, 130],   // 35%  — purple
    [50,  210,   0,  60],   // 50%  — magenta
    [75,  200,  20,  20],   // 75%  — crimson
    [100, 140,   0,   0],   // 100% — dark red
];

// ─── Color Utilities ───────────────────────────────────────────────

function resolveThemeColors(container: HTMLElement) {
    const temp = document.createElement("span");
    temp.style.display = "none";
    container.appendChild(temp);

    const resolve = (varName: string): string => {
        temp.style.color = `var(${varName})`;
        return getComputedStyle(temp).color;
    };

    const colors = {
        mutedFg: resolve("--muted-foreground"),
        foreground: resolve("--foreground"),
        destructive: resolve("--destructive"),
        background: resolve("--background"),
    };

    container.removeChild(temp);
    return colors;
}

function withAlpha(rgbColor: string, alpha: number): string {
    const m = rgbColor.match(/rgba?\(\s*(\d+),\s*(\d+),\s*(\d+)/);
    if (!m) return rgbColor;
    return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${alpha})`;
}

/**
 * SmokePing-style color: map packet loss percentage to a hue
 * on the green→blue→violet→red cool-spectrum scale.
 */
function lossColor(lossPct: number, alpha: number): string {
    const stops = LOSS_COLOR_STOPS;
    if (lossPct <= stops[0][0]) {
        return `rgba(${stops[0][1]}, ${stops[0][2]}, ${stops[0][3]}, ${alpha})`;
    }
    const last = stops[stops.length - 1];
    if (lossPct >= last[0]) {
        return `rgba(${last[1]}, ${last[2]}, ${last[3]}, ${alpha})`;
    }
    for (let i = 0; i < stops.length - 1; i++) {
        if (lossPct >= stops[i][0] && lossPct <= stops[i + 1][0]) {
            const t =
                (lossPct - stops[i][0]) / (stops[i + 1][0] - stops[i][0]);
            const r = Math.round(stops[i][1] + t * (stops[i + 1][1] - stops[i][1]));
            const g = Math.round(stops[i][2] + t * (stops[i + 1][2] - stops[i][2]));
            const b = Math.round(stops[i][3] + t * (stops[i + 1][3] - stops[i][3]));
            return `rgba(${r}, ${g}, ${b}, ${alpha})`;
        }
    }
    return `rgba(38, 198, 98, ${alpha})`;
}

/** Return raw RGB tuple for a loss percentage (for overlay dot). */
function lossColorRGB(lossPct: number): [number, number, number] {
    const stops = LOSS_COLOR_STOPS;
    if (lossPct <= stops[0][0]) return [stops[0][1], stops[0][2], stops[0][3]];
    const last = stops[stops.length - 1];
    if (lossPct >= last[0]) return [last[1], last[2], last[3]];
    for (let i = 0; i < stops.length - 1; i++) {
        if (lossPct >= stops[i][0] && lossPct <= stops[i + 1][0]) {
            const t = (lossPct - stops[i][0]) / (stops[i + 1][0] - stops[i][0]);
            return [
                Math.round(stops[i][1] + t * (stops[i + 1][1] - stops[i][1])),
                Math.round(stops[i][2] + t * (stops[i + 1][2] - stops[i][2])),
                Math.round(stops[i][3] + t * (stops[i + 1][3] - stops[i][3])),
            ];
        }
    }
    return [38, 198, 98];
}

// ─── Formatting ────────────────────────────────────────────────

function formatLatency(ms: number): string {
    if (ms < 1) return `${(ms * 1000).toFixed(0)} us`;
    if (ms < 10) return `${ms.toFixed(2)} ms`;
    if (ms < 100) return `${ms.toFixed(1)} ms`;
    if (ms < 1000) return `${ms.toFixed(0)} ms`;
    return `${(ms / 1000).toFixed(2)} s`;
}

function formatTime(ts: number, rangeSec: number): string {
    const d = new Date(ts * 1000);
    if (rangeSec <= 3600) {
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" });
    }
    if (rangeSec <= 86400) {
        return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    }
    if (rangeSec <= 604800) {
        const day = d.toLocaleDateString([], { weekday: "short" });
        const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
        return `${day} ${time}`;
    }
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
}

// ─── Scale Utilities ───────────────────────────────────────────────

function niceScale(maxVal: number): { max: number; ticks: number[] } {
    if (maxVal <= 0) return { max: 10, ticks: [0, 2, 4, 6, 8, 10] };
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxVal)));
    const norm = maxVal / magnitude;
    let niceMax: number;
    if (norm <= 1.2) niceMax = 1.5 * magnitude;
    else if (norm <= 2) niceMax = 2 * magnitude;
    else if (norm <= 3) niceMax = 3 * magnitude;
    else if (norm <= 5) niceMax = 5 * magnitude;
    else if (norm <= 7) niceMax = 7.5 * magnitude;
    else niceMax = 10 * magnitude;
    const tickCount = 5;
    const step = niceMax / tickCount;
    const ticks: number[] = [];
    for (let i = 0; i <= tickCount; i++) ticks.push(+(step * i).toFixed(6));
    return { max: niceMax, ticks };
}

function computeTimeLabels(start: number, end: number, plotWidth: number): number[] {
    const range = end - start;
    const targetCount = Math.max(3, Math.min(8, Math.floor(plotWidth / 120)));
    const intervals = [10, 15, 30, 60, 120, 300, 600, 900, 1800, 3600, 7200, 14400, 21600, 43200, 86400, 172800, 604800];
    let bestInterval = intervals[intervals.length - 1];
    for (const iv of intervals) {
        if (range / iv <= targetCount + 1) { bestInterval = iv; break; }
    }
    const firstTick = Math.ceil(start / bestInterval) * bestInterval;
    const labels: number[] = [];
    for (let ts = firstTick; ts <= end; ts += bestInterval) labels.push(ts);
    return labels;
}

function findNearestIndex(data: [number, number][], targetTs: number): number {
    if (data.length === 0) return -1;
    let lo = 0;
    let hi = data.length - 1;
    while (lo < hi) {
        const mid = (lo + hi) >> 1;
        if (data[mid][0] < targetTs) lo = mid + 1;
        else hi = mid;
    }
    if (lo > 0 && Math.abs(data[lo - 1][0] - targetTs) < Math.abs(data[lo][0] - targetTs)) {
        return lo - 1;
    }
    return lo;
}

// ─── Plot Geometry ─────────────────────────────────────────────────

interface PlotGeom {
    plotLeft: number;
    plotRight: number;
    plotTop: number;
    plotBottom: number;
    plotWidth: number;
    plotHeight: number;
    timeStart: number;
    timeEnd: number;
    rangeSec: number;
    maxMs: number;
}

// ─── Lookup: build timestamp → loss% map ──────────────────────────

function buildLossMap(packetLoss: [number, number][]): Map<number, number> {
    const map = new Map<number, number>();
    for (const [ts, loss] of packetLoss) {
        map.set(ts, loss);
    }
    return map;
}

/** Find the loss% at a given timestamp, doing nearest-neighbor if exact match fails. */
function getLossAtTimestamp(
    ts: number,
    packetLoss: [number, number][],
    lossMap: Map<number, number>,
): number {
    const exact = lossMap.get(ts);
    if (exact !== undefined) return exact;
    // Nearest-neighbor fallback
    if (packetLoss.length === 0) return 0;
    const idx = findNearestIndex(packetLoss, ts);
    if (idx < 0) return 0;
    return packetLoss[idx][1];
}

// ─── Component ─────────────────────────────────────────────────────

export function SmokeGraph({
    data,
    loading = false,
    height = 300,
    compact = false,
    showLegend = false,
}: SmokeGraphProps) {
    const containerRef = useRef<HTMLDivElement>(null);
    const chartCanvasRef = useRef<HTMLCanvasElement>(null);
    const overlayCanvasRef = useRef<HTMLCanvasElement>(null);

    const plotGeomRef = useRef<PlotGeom | null>(null);
    const themeColorsRef = useRef<ReturnType<typeof resolveThemeColors> | null>(null);
    const lossMapRef = useRef<{ map: Map<number, number>; arr: [number, number][] }>({ map: new Map(), arr: [] });

    const [tooltip, setTooltip] = useState<TooltipState | null>(null);
    const [canvasWidth, setCanvasWidth] = useState(0);
    const [themeKey, setThemeKey] = useState(0);

    const margins = useMemo(
        () =>
            compact
                ? { top: 16, right: 8, bottom: 20, left: 44 }
                : { top: 24, right: 16, bottom: 32, left: 60 },
        [compact],
    );
    const plAreaH = compact ? 14 : 22;

    // ── Resize observer ───────────────────────────────────────
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        let raf: number;
        const ro = new ResizeObserver((entries) => {
            cancelAnimationFrame(raf);
            raf = requestAnimationFrame(() => {
                for (const e of entries) setCanvasWidth(e.contentRect.width);
            });
        });
        ro.observe(el);
        setCanvasWidth(el.clientWidth);
        return () => { cancelAnimationFrame(raf); ro.disconnect(); };
    }, []);

    // ── Theme change detection ─────────────────────────────────
    useEffect(() => {
        const html = document.documentElement;
        const mo = new MutationObserver(() => setThemeKey((k) => k + 1));
        mo.observe(html, { attributes: true, attributeFilter: ["class"] });
        return () => mo.disconnect();
    }, []);

    // ── Pre-process data ───────────────────────────────────────
    const processed = useMemo(() => {
        if (!data) return null;
        let maxMs = 0;
        for (const key of PERCENTILE_KEYS) {
            const s = data.percentiles[key];
            if (s) {
                for (const [, v] of s) {
                    const ms = v * 1000;
                    if (ms > maxMs) maxMs = ms;
                }
            }
        }
        const scale = niceScale(maxMs || 10);

        // Build loss lookup
        const lossMap = buildLossMap(data.packet_loss || []);

        return {
            maxMs: scale.max,
            ticks: scale.ticks,
            tStart: data.time_range.start,
            tEnd: data.time_range.end,
            rangeSec: data.time_range.end - data.time_range.start,
            lossMap,
        };
    }, [data]);

    // ── Draw chart ─────────────────────────────────────────────
    useEffect(() => {
        const canvas = chartCanvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container || !data || !processed || canvasWidth <= 0)
            return;

        const dpr = window.devicePixelRatio || 1;
        const w = canvasWidth;
        const h = height;
        canvas.width = w * dpr;
        canvas.height = h * dpr;
        canvas.style.width = `${w}px`;
        canvas.style.height = `${h}px`;

        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

        const tc = resolveThemeColors(container);
        themeColorsRef.current = tc;
        const monoFont = "'JetBrains Mono', ui-monospace, monospace";
        const uiFont = "Inter, system-ui, -apple-system, sans-serif";

        // Store loss refs for tooltip
        lossMapRef.current = { map: processed.lossMap, arr: data.packet_loss || [] };

        const pL = margins.left;
        const pR = w - margins.right;
        const pT = margins.top + plAreaH;
        const pB = h - margins.bottom;
        const pW = pR - pL;
        const pH = pB - pT;

        plotGeomRef.current = {
            plotLeft: pL, plotRight: pR, plotTop: pT, plotBottom: pB,
            plotWidth: pW, plotHeight: pH,
            timeStart: processed.tStart, timeEnd: processed.tEnd,
            rangeSec: processed.rangeSec, maxMs: processed.maxMs,
        };

        const xS = (ts: number) => pL + ((ts - processed.tStart) / processed.rangeSec) * pW;
        const yS = (ms: number) => pB - (ms / processed.maxMs) * pH;

        ctx.clearRect(0, 0, w, h);

        // ── Grid lines ──────────────────────────────────────
        ctx.lineWidth = 1;
        ctx.strokeStyle = withAlpha(tc.mutedFg, 0.06);

        for (const tick of processed.ticks) {
            const y = Math.round(yS(tick)) + 0.5;
            ctx.beginPath(); ctx.moveTo(pL, y); ctx.lineTo(pR, y); ctx.stroke();
        }

        const timeLabels = computeTimeLabels(processed.tStart, processed.tEnd, pW);
        for (const ts of timeLabels) {
            const x = Math.round(xS(ts)) + 0.5;
            ctx.beginPath(); ctx.moveTo(x, pT); ctx.lineTo(x, pB); ctx.stroke();
        }

        // ── Smoke bands (per-column, loss-colored) ──────────
        //
        // For each band, instead of one continuous fill, we draw
        // per-timestamp-column trapezoids. Each column's color is
        // determined by the packet loss at that timestamp.
        // This is the defining SmokePing visual: the smoke shifts
        // color from green → blue → violet → red as loss increases.

        const pl = data.packet_loss || [];

        for (const band of SMOKE_BANDS) {
            const outer = data.percentiles[band.outer];
            const inner = data.percentiles[band.inner];
            if (!outer?.length || !inner?.length) continue;

            // Both arrays should share timestamps — use inner as reference
            const len = Math.min(inner.length, outer.length);

            for (let i = 0; i < len - 1; i++) {
                const ts = inner[i][0];
                const tsNext = inner[i + 1][0];

                // Look up loss at this timestamp
                const loss = getLossAtTimestamp(ts, pl, processed.lossMap);

                // Color from loss, at this band's alpha
                ctx.fillStyle = lossColor(loss, band.alpha);

                // Draw trapezoid: inner[i] → inner[i+1] → outer[i+1] → outer[i]
                const ix1 = xS(ts);
                const ix2 = xS(tsNext);
                const iy1 = yS(inner[i][1] * 1000);
                const iy2 = yS(inner[i + 1][1] * 1000);
                const oy1 = yS(outer[i][1] * 1000);
                const oy2 = yS(outer[i + 1][1] * 1000);

                ctx.beginPath();
                ctx.moveTo(ix1, iy1);
                ctx.lineTo(ix2, iy2);
                ctx.lineTo(ix2, oy2);
                ctx.lineTo(ix1, oy1);
                ctx.closePath();
                ctx.fill();
            }
        }

        // ── Median (p50) line — subtle, loss-colored per segment ──
        const p50 = data.percentiles["p50"];
        if (p50 && p50.length > 1) {
            ctx.lineWidth = compact ? 1.5 : 2;
            ctx.lineJoin = "round";
            ctx.lineCap = "round";

            for (let i = 0; i < p50.length - 1; i++) {
                const ts = p50[i][0];
                const loss = getLossAtTimestamp(ts, pl, processed.lossMap);

                ctx.strokeStyle = lossColor(loss, 0.9);
                ctx.beginPath();
                ctx.moveTo(xS(p50[i][0]), yS(p50[i][1] * 1000));
                ctx.lineTo(xS(p50[i + 1][0]), yS(p50[i + 1][1] * 1000));
                ctx.stroke();
            }
        }

        // ── Packet loss bars at top (secondary indicator) ───
        if (pl.length > 1) {
            const barW = Math.max(2, pW / pl.length);
            const plTop = margins.top;
            const plBot = margins.top + plAreaH;

            for (const pt of pl) {
                const loss = pt[1];
                if (loss <= 0) continue;

                const x = xS(pt[0]);
                const barH = Math.max(2, (Math.min(loss, 100) / 100) * (plBot - plTop));

                // Use the same loss color scale for bars
                ctx.fillStyle = lossColor(loss, loss >= 10 ? 0.85 : loss >= 2 ? 0.55 : 0.35);
                ctx.fillRect(x - barW / 2, plBot - barH, barW, barH);
            }
        }

        // ── Y-axis labels ───────────────────────────────────
        const fontSize = compact ? 10 : 11;
        ctx.font = `${fontSize}px ${monoFont}`;
        ctx.textAlign = "right";
        ctx.textBaseline = "middle";
        ctx.fillStyle = withAlpha(tc.mutedFg, 0.6);

        for (const tick of processed.ticks) {
            const y = yS(tick);
            if (y < pT - 5 || y > pB + 5) continue;
            ctx.fillText(formatLatency(tick), pL - 8, y);
        }

        // ── X-axis labels ───────────────────────────────────
        ctx.font = `${fontSize}px ${monoFont}`;
        ctx.textAlign = "center";
        ctx.textBaseline = "top";

        for (const ts of timeLabels) {
            ctx.fillText(formatTime(ts, processed.rangeSec), xS(ts), pB + 6);
        }

        // ── Axis lines ──────────────────────────────────────
        ctx.strokeStyle = withAlpha(tc.mutedFg, 0.12);
        ctx.lineWidth = 1;

        ctx.beginPath(); ctx.moveTo(pL + 0.5, pT); ctx.lineTo(pL + 0.5, pB); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(pL, pB + 0.5); ctx.lineTo(pR, pB + 0.5); ctx.stroke();

        // ── "LOSS" label ────────────────────────────────────
        if (!compact) {
            ctx.font = `600 8px ${uiFont}`;
            ctx.textAlign = "left";
            ctx.textBaseline = "top";
            ctx.fillStyle = withAlpha(tc.mutedFg, 0.35);
            ctx.fillText("LOSS", pL + 3, margins.top + 2);
        }

        ctx.setTransform(1, 0, 0, 1, 0, 0);
    }, [data, processed, canvasWidth, height, compact, margins, plAreaH, themeKey]);

    // ── Overlay canvas sizing ──────────────────────────────────
    useEffect(() => {
        const overlay = overlayCanvasRef.current;
        if (!overlay) return;
        const dpr = window.devicePixelRatio || 1;
        overlay.width = canvasWidth * dpr;
        overlay.height = height * dpr;
        overlay.style.width = `${canvasWidth}px`;
        overlay.style.height = `${height}px`;
    }, [canvasWidth, height]);

    // ── Mouse interaction ──────────────────────────────────────
    const handleMouseMove = useCallback(
        (e: React.MouseEvent<HTMLCanvasElement>) => {
            const overlay = overlayCanvasRef.current;
            const container = containerRef.current;
            const geom = plotGeomRef.current;
            const tc = themeColorsRef.current;

            if (!overlay || !container || !data || !geom || !tc) {
                setTooltip(null);
                return;
            }

            const rect = overlay.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;

            if (mx < geom.plotLeft || mx > geom.plotRight || my < margins.top || my > geom.plotBottom) {
                const ctx = overlay.getContext("2d");
                if (ctx) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, overlay.width, overlay.height); }
                setTooltip(null);
                return;
            }

            const frac = (mx - geom.plotLeft) / (geom.plotRight - geom.plotLeft);
            const ts = geom.timeStart + frac * geom.rangeSec;

            const values: Record<string, number | null> = {};
            for (const key of PERCENTILE_KEYS) {
                const series = data.percentiles[key];
                if (!series?.length) { values[key] = null; continue; }
                const idx = findNearestIndex(series, ts);
                values[key] = idx >= 0 ? series[idx][1] * 1000 : null;
            }

            let plVal: number | null = null;
            if (data.packet_loss?.length) {
                const idx = findNearestIndex(data.packet_loss, ts);
                if (idx >= 0) plVal = data.packet_loss[idx][1];
            }

            const dpr = window.devicePixelRatio || 1;
            const ctx = overlay.getContext("2d");
            if (ctx) {
                ctx.setTransform(1, 0, 0, 1, 0, 0);
                ctx.clearRect(0, 0, overlay.width, overlay.height);
                ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

                // Vertical dashed crosshair
                ctx.strokeStyle = withAlpha(tc.foreground, 0.15);
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 3]);
                ctx.beginPath();
                ctx.moveTo(Math.round(mx) + 0.5, geom.plotTop);
                ctx.lineTo(Math.round(mx) + 0.5, geom.plotBottom);
                ctx.stroke();
                ctx.setLineDash([]);

                // Dot on p50 line — colored by loss at this timestamp
                if (values.p50 != null) {
                    const dotY = geom.plotBottom - (values.p50 / geom.maxMs) * geom.plotHeight;
                    const currentLoss = plVal ?? 0;
                    const [cr, cg, cb] = lossColorRGB(currentLoss);

                    ctx.fillStyle = `rgba(${cr}, ${cg}, ${cb}, 0.95)`;
                    ctx.beginPath();
                    ctx.arc(mx, dotY, 4, 0, Math.PI * 2);
                    ctx.fill();
                    ctx.strokeStyle = withAlpha(tc.background, 0.8);
                    ctx.lineWidth = 2;
                    ctx.stroke();
                }

                ctx.setTransform(1, 0, 0, 1, 0, 0);
            }

            setTooltip({ x: mx, y: my, timestamp: ts, values, packetLoss: plVal });
        },
        [data, margins.top],
    );

    const handleMouseLeave = useCallback(() => {
        const overlay = overlayCanvasRef.current;
        if (overlay) {
            const ctx = overlay.getContext("2d");
            if (ctx) { ctx.setTransform(1, 0, 0, 1, 0, 0); ctx.clearRect(0, 0, overlay.width, overlay.height); }
        }
        setTooltip(null);
    }, []);

    // ── Render states ──────────────────────────────────────────

    if (loading) {
        return (
            <div ref={containerRef} className="w-full animate-pulse rounded-lg bg-muted/20" style={{ height }} />
        );
    }

    if (!data || !Object.values(data.percentiles).some(arr => arr?.length > 0)) {
        return (
            <div
                ref={containerRef}
                className="flex w-full items-center justify-center rounded-lg border border-dashed border-border/50 text-sm text-muted-foreground"
                style={{ height }}
            >
                No monitoring data available
            </div>
        );
    }

    const tooltipStyle: React.CSSProperties | null = tooltip
        ? (() => {
              const s: React.CSSProperties = {};
              if (tooltip.x > canvasWidth - 220) s.right = canvasWidth - tooltip.x + 16;
              else s.left = tooltip.x + 16;
              if (tooltip.y > height - 160) s.bottom = height - tooltip.y + 8;
              else s.top = Math.max(4, tooltip.y - 8);
              return s;
          })()
        : null;

    // Loss color at tooltip position for the indicator dot
    const tooltipLoss = tooltip?.packetLoss ?? 0;
    const [tlR, tlG, tlB] = lossColorRGB(tooltipLoss);

    return (
        <div
            ref={containerRef}
            className="relative w-full select-none"
            style={{ height: showLegend ? height + 36 : height }}
            role="img"
            aria-label={`Smoke graph showing latency percentiles${data.stats ? `. Median: ${formatLatency(data.stats.current_latency_ms)}, Packet loss: ${data.stats.avg_packet_loss?.toFixed(1)}%` : ""}`}
        >
            <div style={{ height }}>
                <canvas ref={chartCanvasRef} className="absolute inset-0" />
                <canvas
                    ref={overlayCanvasRef}
                    className="absolute inset-0 cursor-crosshair"
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    onPointerMove={handleMouseMove}
                    onPointerLeave={handleMouseLeave}
                />
            </div>

            {/* Tooltip */}
            {tooltip && tooltipStyle && (
                <div
                    className="pointer-events-none absolute z-10 min-w-[150px] rounded-md border border-border/50 bg-popover/95 px-3 py-2 shadow-lg backdrop-blur-md"
                    style={tooltipStyle}
                >
                    <div className="mb-1.5 font-data text-[10px] tabular-nums text-muted-foreground">
                        {new Date(tooltip.timestamp * 1000).toLocaleString()}
                    </div>
                    <div className="space-y-0.5 text-xs">
                        {PERCENTILE_KEYS.map((key) => {
                            if (compact && key !== "p50" && key !== "p95") return null;
                            const val = tooltip.values[key];
                            if (val == null) return null;
                            return (
                                <div key={key} className="flex items-center justify-between gap-3 font-data">
                                    <span className="text-muted-foreground">{key}</span>
                                    <span className={key === "p50" ? "font-semibold text-foreground" : "text-muted-foreground"}>
                                        {formatLatency(val)}
                                    </span>
                                </div>
                            );
                        })}
                        {/* Loss with color indicator */}
                        <div className="flex items-center justify-between gap-3 font-data pt-0.5 border-t border-border/30 mt-1">
                            <span className="flex items-center gap-1.5 text-muted-foreground">
                                <span
                                    className="inline-block h-2 w-2 rounded-full"
                                    style={{ backgroundColor: `rgb(${tlR}, ${tlG}, ${tlB})` }}
                                />
                                loss
                            </span>
                            <span className="font-semibold" style={{ color: tooltipLoss > 0 ? `rgb(${tlR}, ${tlG}, ${tlB})` : undefined }}>
                                {(tooltip.packetLoss ?? 0).toFixed(1)}%
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Legend — loss color scale */}
            {showLegend && (
                <div className="flex items-center justify-center gap-5 pt-3 text-[10px] text-muted-foreground">
                    <div className="flex items-center gap-1.5">
                        <span className="h-[2px] w-4 rounded-full" style={{ backgroundColor: `rgb(38, 198, 98)` }} />
                        <span>Median</span>
                    </div>
                    <div className="flex items-center gap-0.5">
                        <span className="text-muted-foreground/60 mr-1">Loss:</span>
                        {[0, 5, 15, 30, 50, 75].map((pct) => {
                            const [r, g, b] = lossColorRGB(pct);
                            return (
                                <span
                                    key={pct}
                                    className="h-2.5 w-3 first:rounded-l-sm last:rounded-r-sm"
                                    style={{ backgroundColor: `rgb(${r}, ${g}, ${b})` }}
                                    title={`${pct}% loss`}
                                />
                            );
                        })}
                        <span className="ml-1 text-muted-foreground/60">0%→100%</span>
                    </div>
                </div>
            )}
        </div>
    );
}
